import { useEffect } from 'react';
import { create } from 'zustand';
import { uiSocket } from '../live/socket';
import { useNow } from './sensors';

/**
 * Live lidar on the radars (PROTOCOL §27). A radar that shows the lidar layer `watch`es its robot over /ws/ui and
 * renews the watch every 10 s; the robot streams only while its shared switch is on and someone watches.
 * Frames: 360 bins of 1° (cm from the lidar centre, 0 = nothing; robot bearing +90 = LEFT) and the objects the robot
 * guard follows (`d` from the body). Everything here is converted to the radar's convention: bearing + = RIGHT.
 */

export interface LidarFrameMsg {
  type: 'lidar';
  serial: string;
  t?: number;
  from?: number;
  binDeg?: number;
  bodyM?: number;
  phase?: string;
  bins?: number[];
  tracks?: { id: number; d: number; b: number }[];
}

export interface LidarStateMsg {
  type: 'lidarState';
  serial: string;
  enabled: boolean;
  watchers: number;
  streaming: boolean;
}

export type LidarTrend = 'approaching' | 'static' | 'receding';

export interface LidarTrack {
  id: string;
  /** metres from the robot's body */
  d: number;
  /** radar bearing, degrees, + = right */
  b: number;
  trend: LidarTrend;
  /** m/s, ≥ 0 */
  speed: number;
  /** seconds it has not moved (static only) */
  stillS: number;
  /** last ~2 s of positions [d, b], oldest first */
  trail: [number, number][];
}

export type LidarStatus = 'off' | 'connecting' | 'live' | 'moving' | 'nodata';

export interface LidarView {
  status: LidarStatus;
  enabled: boolean;
  watchers: number;
  /** wall points [radar bearing, metres from the lidar centre] */
  points: [number, number][];
  bodyM: number;
  tracks: LidarTrack[];
  hz: number | null;
  delayMs: number | null;
}

interface Hist {
  at: number;
  d: number;
  b: number;
}

interface Entry {
  frame: LidarFrameMsg | null;
  frameAt: number;
  frameTimes: number[];
  hist: Record<string, Hist[]>;
  stillSince: Record<string, number>;
  state: LidarStateMsg | null;
  watchSince: number;
}

const TRAIL_MS = 2000;
const TREND_WINDOW_MS = 1000;
const MOVE_MPS = 0.15;
const STALE_MS = 2000;
const CONNECT_MS = 4000;
const RENEW_MS = 10_000;

const empty = (): Entry => ({ frame: null, frameAt: 0, frameTimes: [], hist: {}, stillSince: {}, state: null, watchSince: 0 });

interface LidarStore {
  bySerial: Record<string, Entry>;
  onFrame: (m: LidarFrameMsg) => void;
  onState: (m: LidarStateMsg) => void;
  watching: (serial: string, at: number) => void;
}

export const useLidarStore = create<LidarStore>((set) => ({
  bySerial: {},
  onFrame: (m) =>
    set((s) => {
      const now = Date.now();
      const cur = s.bySerial[m.serial] ?? empty();
      const hist: Record<string, Hist[]> = {};
      const stillSince: Record<string, number> = {};
      for (const tr of m.tracks ?? []) {
        const id = `L-${String(tr.id).padStart(2, '0')}`;
        const prev = cur.hist[id] ?? [];
        const h = [...prev.filter((x) => now - x.at <= TRAIL_MS + TREND_WINDOW_MS), { at: now, d: tr.d, b: -tr.b }];
        hist[id] = h;
        // "stopped for N s": restarts whenever it moved more than 10 cm from where it was a second ago
        const ref = prev.find((x) => now - x.at <= TREND_WINDOW_MS);
        const moved = ref ? Math.abs(ref.d - tr.d) > 0.1 || Math.abs(ref.b + tr.b) * (Math.PI / 180) * tr.d > 0.1 : false;
        stillSince[id] = moved || cur.stillSince[id] === undefined ? now : cur.stillSince[id]!;
      }
      const frameTimes = [...cur.frameTimes.filter((x) => now - x <= 2000), now];
      return { bySerial: { ...s.bySerial, [m.serial]: { ...cur, frame: m, frameAt: now, frameTimes, hist, stillSince } } };
    }),
  onState: (m) =>
    set((s) => {
      const cur = s.bySerial[m.serial] ?? empty();
      // switched back on: wait for a fresh first scan ("connecting"), not "no data" from the old one
      const reopened = m.enabled && cur.state?.enabled === false && cur.watchSince > 0;
      const next = reopened ? { ...cur, state: m, frame: null, hist: {}, watchSince: Date.now() } : { ...cur, state: m };
      return { bySerial: { ...s.bySerial, [m.serial]: next } };
    }),
  watching: (serial, at) =>
    set((s) => {
      const cur = s.bySerial[serial] ?? empty();
      return { bySerial: { ...s.bySerial, [serial]: { ...cur, watchSince: cur.watchSince || at } } };
    }),
}));

/** Trend and speed from the position ~1 s ago (closer = approaching). */
function motion(h: Hist[], now: number): { trend: LidarTrend; speed: number } {
  if (h.length < 2) return { trend: 'static', speed: 0 };
  const last = h[h.length - 1]!;
  const old = h.find((x) => last.at - x.at <= TREND_WINDOW_MS) ?? h[0]!;
  const dt = (last.at - old.at) / 1000;
  if (dt < 0.3 || now - last.at > STALE_MS) return { trend: 'static', speed: 0 };
  const vRad = (old.d - last.d) / dt; // + = closer
  const [ox, oy] = [old.d * Math.sin((old.b * Math.PI) / 180), old.d * Math.cos((old.b * Math.PI) / 180)];
  const [lx, ly] = [last.d * Math.sin((last.b * Math.PI) / 180), last.d * Math.cos((last.b * Math.PI) / 180)];
  const speed = Math.hypot(lx - ox, ly - oy) / dt;
  if (vRad > MOVE_MPS) return { trend: 'approaching', speed };
  if (vRad < -MOVE_MPS) return { trend: 'receding', speed };
  return { trend: 'static', speed: speed > MOVE_MPS ? speed : 0 };
}

/** The radar's lidar layer for `serial` (pure: the store entry + the clock). */
export function lidarView(e: Entry | undefined, now: number): LidarView {
  const enabled = e?.state?.enabled ?? true;
  const fresh = Boolean(e?.frame) && now - (e?.frameAt ?? 0) <= STALE_MS;
  const status: LidarStatus = !enabled
    ? 'off'
    : !fresh
      ? e?.watchSince && !e.frame && now - e.watchSince < CONNECT_MS
        ? 'connecting'
        : 'nodata'
      : e!.frame!.phase === 'moving'
        ? 'moving'
        : 'live';
  const f = fresh ? e!.frame! : null;
  const from = f?.from ?? -180;
  const step = f?.binDeg ?? 1;
  const points: [number, number][] = [];
  (f?.bins ?? []).forEach((cm, i) => {
    if (cm > 0) points.push([-(from + (i + 0.5) * step), cm / 100]);
  });
  const tracks: LidarTrack[] = [];
  if (f && e)
    for (const [id, h] of Object.entries(e.hist)) {
      const last = h[h.length - 1]!;
      const { trend, speed } = motion(h, now);
      tracks.push({
        id,
        d: last.d,
        b: last.b,
        trend,
        speed,
        stillS: trend === 'static' ? Math.max(0, (now - (e.stillSince[id] ?? now)) / 1000) : 0,
        trail: h.filter((x) => last.at - x.at <= TRAIL_MS).slice(0, -1).map((x) => [x.d, x.b]),
      });
    }
  tracks.sort((a, b) => a.d - b.d);
  const n = e?.frameTimes.length ?? 0;
  const hz = fresh && n >= 2 ? ((n - 1) * 1000) / Math.max(1, e!.frameTimes[n - 1]! - e!.frameTimes[0]!) : null;
  const delay = f?.t ? (e!.frameAt - f.t) : null;
  return {
    status,
    enabled,
    watchers: e?.state?.watchers ?? 0,
    points,
    bodyM: f?.bodyM ?? 0.3,
    tracks,
    hz,
    delayMs: delay !== null && delay >= 0 && delay < 5000 ? delay : null,
  };
}

// ------------------------------------------------------------------ watching (ref-counted per robot)

const watchers = new Map<string, { n: number; timer: ReturnType<typeof setInterval> }>();

function sendWatch(serial: string) {
  uiSocket.send({ type: 'lidar', serial, action: 'watch' });
}

uiSocket.onOpen(() => {
  for (const serial of watchers.keys()) sendWatch(serial);
});

uiSocket.on((m) => {
  const msg = m as unknown as { type: string; serial?: unknown };
  if (typeof msg.serial !== 'string') return;
  if (msg.type === 'lidar') useLidarStore.getState().onFrame(msg as LidarFrameMsg);
  else if (msg.type === 'lidarState') useLidarStore.getState().onState(msg as LidarStateMsg);
});

function watch(serial: string) {
  const w = watchers.get(serial);
  if (w) {
    w.n++;
    return;
  }
  useLidarStore.getState().watching(serial, Date.now());
  sendWatch(serial);
  watchers.set(serial, { n: 1, timer: setInterval(() => sendWatch(serial), RENEW_MS) });
}

function unwatch(serial: string) {
  const w = watchers.get(serial);
  if (!w) return;
  if (--w.n > 0) return;
  clearInterval(w.timer);
  watchers.delete(serial);
  uiSocket.send({ type: 'lidar', serial, action: 'unwatch' });
  useLidarStore.setState((s) => {
    const cur = s.bySerial[serial];
    return cur ? { bySerial: { ...s.bySerial, [serial]: { ...cur, watchSince: 0, frame: null, hist: {} } } } : s;
  });
}

/** The robot's shared lidar switch (every viewer of this robot sees the change). */
export function setLidarEnabled(serial: string, enabled: boolean) {
  uiSocket.send({ type: 'lidar', serial, action: 'set', enabled });
}

/** Watch `serial`'s lidar while `active`; returns its live layer (re-evaluated 4× a second for the staleness checks). */
export function useLidar(serial: string, active: boolean): LidarView | null {
  useEffect(() => {
    if (!active || !serial) return;
    watch(serial);
    return () => unwatch(serial);
  }, [serial, active]);
  const e = useLidarStore((s) => s.bySerial[serial]);
  const now = useNow(250);
  if (!active) return null;
  return lidarView(e, Math.max(now, e?.frameAt ?? 0));
}

// ------------------------------------------------------------------ radar scale (Sensors page, mini-radar)

/** A fixed range in metres, or "max": fit the farthest return the lidar sees right now. */
export type LidarRange = number | 'max';

/** Zoom steps (metres); wheel / − + move along them. */
export const LIDAR_STEPS = [1, 1.5, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30];

/** Metres to the farthest wall point (from the lidar centre), or null without points. */
export function lidarFarthest(l: LidarView | null): number | null {
  if (!l || !l.points.length) return null;
  let m = 0;
  for (const [, r] of l.points) if (r > m) m = r;
  return m;
}

/** The radar range for a setting: "max" fits the farthest point (+5 %, rounded up to 0.5 m; 4 m without data). */
export function lidarRangeOf(setting: LidarRange, l: LidarView | null): number {
  if (setting !== 'max') return setting;
  const far = lidarFarthest(l);
  return far === null ? 4 : Math.max(1, Math.ceil(far * 1.05 * 2) / 2);
}

/** One zoom step from `current` metres: `dir` −1 = closer (smaller range), +1 = farther. */
export function zoomRange(current: number, dir: -1 | 1): number {
  if (dir < 0) return [...LIDAR_STEPS].reverse().find((s) => s < current - 1e-6) ?? LIDAR_STEPS[0]!;
  return LIDAR_STEPS.find((s) => s > current + 1e-6) ?? LIDAR_STEPS[LIDAR_STEPS.length - 1]!;
}
